'use client';

import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { env } from '@repo/platform-env';
import { useRouter } from 'next/navigation';
import { type MutableRefObject, type TransitionStartFunction, useCallback } from 'react';
import { useAnswerDispatch, useIsAnswerHydrated, useSubmittedAnswers } from '@/entities/answer/model/hooks';
import type { BranchId } from '@/shared/config/funnel/constants';
import { ENTRY_STEP_ID } from '@/shared/config/funnel/flowConstants';
import { saveFunnelSession } from '../api/saveFunnelSession';
import { abandonFunnelRun } from './abandonFunnelRun';
import { readLocalSession, shouldRotateLocalSession } from './localSession';
const hasAnsweredBeyondEntry = (answers: AnswerState) => Object.keys(answers).some(stepId => stepId !== ENTRY_STEP_ID);

/**
 * Catches up an unfinished run's session write once on login, and only for
 * an unfinished run. Returns the catch-up for the funnel to call whenever its
 * inputs change; `hasSyncedOnLoginRef` is shared with `useAnswerSubmissionSync`.
 */
export function useSyncOnLogin(hasSyncedOnLoginRef) {
  const $ = _c(7);
  const isSessionPersistenceEnabled = env.NEXT_PUBLIC_ENABLE_SESSION_PERSISTENCE;
  const {
    isLoggedIn
  } = useIdentityContext();
  const router = useRouter();
  const dispatch = useAnswerDispatch();
  const isHydrated = useIsAnswerHydrated();
  const submittedAnswers = useSubmittedAnswers();
  let t0;
  if ($[0] !== dispatch || $[1] !== hasSyncedOnLoginRef || $[2] !== isHydrated || $[3] !== isLoggedIn || $[4] !== router || $[5] !== submittedAnswers) {
    t0 = (branchId, funnelConfigVersion, startFunnelTransition) => {
      if (!branchId || !isSessionPersistenceEnabled || !isLoggedIn || !isHydrated) {
        return;
      }
      if (hasSyncedOnLoginRef.current) {
        return;
      }
      if (!hasAnsweredBeyondEntry(submittedAnswers)) {
        return;
      }
      if (shouldRotateLocalSession(readLocalSession())) {
        return;
      }
      hasSyncedOnLoginRef.current = true;
      startFunnelTransition(async () => {
        const {
          result
        } = await saveFunnelSession(submittedAnswers, {
          funnelConfigVersion
        });
        if (result === "version-mismatch") {
          abandonFunnelRun(dispatch, router);
        }
        if (result === "error") {
          datadogRum.addError(new Error("Login catch-up session save failed"), {
            branchId,
            funnelConfigVersion
          });
        }
      });
    };
    $[0] = dispatch;
    $[1] = hasSyncedOnLoginRef;
    $[2] = isHydrated;
    $[3] = isLoggedIn;
    $[4] = router;
    $[5] = submittedAnswers;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}